import { useRef, useEffect, useCallback } from "react";
import { Dropdown } from "primereact/dropdown";
import { CustomCellEditorProps, useGridCellEditor } from "ag-grid-react";

// Editor name type — used in AddressCardColumns.ts to specify which editor a column uses.
export type AgEditorName =
  | "agTextCellEditor"
  | "agLargeTextCellEditor"
  | "agNumberCellEditor"
  | "agFilterableSelectCellEditor";

export interface FilterableSelectEditorParams {
  values: { label: string; value: string }[] | string[];
  filter?: boolean;
}

// AG Grid custom cell editor that wraps PrimeReact Dropdown.
// Supports optional search/filter for large option lists (SIC code, state).
export const FilterableSelectCellEditor = (
  props: CustomCellEditorProps<unknown, string> & FilterableSelectEditorParams
) => {
  const { value, initialValue, onValueChange, values, filter } = props;
  const dropdownRef = useRef<Dropdown>(null);
  const panelWidth = Math.max(props.column.getActualWidth(), 180);

  useEffect(() => {
    // Open the dropdown panel automatically when the editor mounts
    setTimeout(() => dropdownRef.current?.show(), 0);
  }, []);

  const isCancelAfterEnd = useCallback(() => {
    // Cancel the edit if the value didn't change — prevents clobbering
    // non-standard values when the user opens and closes without picking.
    return value === initialValue;
  }, [value, initialValue]);

  useGridCellEditor({ isCancelAfterEnd });

  return (
    <Dropdown
      ref={dropdownRef}
      style={{ width: "100%", height: "100%", border: "none", borderRadius: 0 }}
      options={values}
      value={value}
      onChange={(e) => {
        onValueChange(e.value);
        // Commit the edit immediately on selection so the user doesn't
        // have to click away or press Enter.
        setTimeout(() => props.api.stopEditing(), 0);
      }}
      filter={filter}
      autoFocus
      scrollHeight="400px"
      pt={{
        panel: {
          style: { width: `${panelWidth}px`, overflow: "hidden" },
        },
        wrapper: {
          style: { overflowX: "hidden" },
        },
        item: {
          style: {
            padding: "0.4rem 0.75rem",
            fontSize: "0.85rem",
            whiteSpace: "normal",
            wordBreak: "break-word",
          },
        },
      }}
    />
  );
};
